import { useEffect, useState } from "react";
import { Plus, Building2, UserPlus, ShieldCheck, X } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

export default function Admin() {
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [companyOpen, setCompanyOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const [assignFor, setAssignFor] = useState(null);
  const [cForm, setCForm] = useState({ name: "", gst_number: "", filing_frequency: "quarterly", address: "" });
  const [uForm, setUForm] = useState({ name: "", email: "", password: "", is_admin: false });
  const [assign, setAssign] = useState({ company_id: "", role: "member" });

  const load = () => {
    api.get("/admin/companies").then((r) => setCompanies(r.data));
    api.get("/admin/users").then((r) => setUsers(r.data));
  };
  useEffect(() => {
    load();
  }, []);

  const createCompany = async () => {
    if (!cForm.name.trim()) return toast.error("Company name required");
    try {
      await api.post("/admin/companies", cForm);
      toast.success("Company created with a standard Chart of Accounts");
      setCompanyOpen(false);
      setCForm({ name: "", gst_number: "", filing_frequency: "quarterly", address: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create company");
    }
  };

  const createUser = async () => {
    if (!uForm.name.trim() || !uForm.email.trim() || !uForm.password.trim())
      return toast.error("Name, email and password required");
    try {
      await api.post("/admin/users", uForm);
      toast.success("User created");
      setUserOpen(false);
      setUForm({ name: "", email: "", password: "", is_admin: false });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create user");
    }
  };

  const addMembership = async () => {
    if (!assign.company_id) return toast.error("Pick a company");
    try {
      await api.post("/admin/memberships", { user_id: assignFor.id, ...assign });
      toast.success("Access granted");
      setAssignFor(null);
      setAssign({ company_id: "", role: "member" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not assign");
    }
  };

  const removeMembership = async (userId, companyId) => {
    await api.delete(`/admin/memberships?user_id=${userId}&company_id=${companyId}`);
    load();
  };

  const companyName = (id) => companies.find((c) => c.id === id)?.name || id;

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
          <ShieldCheck className="h-7 w-7" /> Admin Console
        </h1>
        <p className="text-slate-500 text-sm mt-1">Set up companies first, then create users and grant access.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Companies */}
        <Card className="rounded-lg border-slate-200 shadow-sm p-0 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
            <h2 className="font-heading font-semibold text-slate-900 flex items-center gap-2">
              <Building2 className="h-4 w-4" /> Companies
            </h2>
            <Button size="sm" data-testid="admin-add-company-btn" onClick={() => setCompanyOpen(true)} className="rounded-md bg-slate-900 hover:bg-slate-800">
              <Plus className="h-4 w-4 mr-1" /> Company
            </Button>
          </div>
          <table className="w-full text-sm" data-testid="admin-companies-table">
            <tbody>
              {companies.map((c) => (
                <tr key={c.id} className="border-b border-slate-50">
                  <td className="px-5 py-3 font-medium text-slate-900">
                    {c.name}
                    <span className="block text-xs text-slate-400">{c.gst_number || "No GST #"} · {c.filing_frequency}</span>
                  </td>
                  <td className="px-5 py-3 text-right text-slate-500 text-xs">{c.member_count} member(s)</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        {/* Users */}
        <Card className="rounded-lg border-slate-200 shadow-sm p-0 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
            <h2 className="font-heading font-semibold text-slate-900 flex items-center gap-2">
              <UserPlus className="h-4 w-4" /> Users & Access
            </h2>
            <Button size="sm" data-testid="admin-add-user-btn" onClick={() => setUserOpen(true)} className="rounded-md bg-slate-900 hover:bg-slate-800">
              <Plus className="h-4 w-4 mr-1" /> User
            </Button>
          </div>
          <div className="divide-y divide-slate-50" data-testid="admin-users-list">
            {users.map((u) => (
              <div key={u.id} className="px-5 py-3">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-medium text-slate-900">{u.name}</span>
                    {u.is_admin && <Badge variant="outline" className="ml-2 bg-slate-900 text-white border-slate-900">Admin</Badge>}
                    <span className="block text-xs text-slate-400">{u.email}</span>
                  </div>
                  <Button variant="outline" size="sm" data-testid={`assign-user-${u.id}`} onClick={() => setAssignFor(u)} className="rounded-md">
                    Grant access
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {u.memberships.length === 0 && <span className="text-xs text-slate-400">No company access</span>}
                  {u.memberships.map((m) => (
                    <Badge key={m.company_id} variant="outline" className="bg-slate-50 text-slate-600 border-slate-200 gap-1">
                      {companyName(m.company_id)} · {m.role}
                      <button data-testid={`remove-membership-${u.id}-${m.company_id}`} onClick={() => removeMembership(u.id, m.company_id)}>
                        <X className="h-3 w-3 ml-1 hover:text-rose-600" />
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Create company */}
      <Dialog open={companyOpen} onOpenChange={setCompanyOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-heading">New Company</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Company Name</Label>
              <Input data-testid="admin-company-name" value={cForm.name} onChange={(e) => setCForm({ ...cForm, name: e.target.value })} className="mt-1 rounded-md" />
            </div>
            <div>
              <Label>Address</Label>
              <Input data-testid="admin-company-address" value={cForm.address} onChange={(e) => setCForm({ ...cForm, address: e.target.value })} className="mt-1 rounded-md" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>GST Number</Label>
                <Input data-testid="admin-company-gst" value={cForm.gst_number} onChange={(e) => setCForm({ ...cForm, gst_number: e.target.value })} className="mt-1 rounded-md" />
              </div>
              <div>
                <Label>Filing Frequency</Label>
                <Select value={cForm.filing_frequency} onValueChange={(v) => setCForm({ ...cForm, filing_frequency: v })}>
                  <SelectTrigger data-testid="admin-company-freq" className="mt-1 rounded-md capitalize"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="annual">Annual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-md" onClick={() => setCompanyOpen(false)}>Cancel</Button>
            <Button data-testid="admin-save-company" onClick={createCompany} className="rounded-md bg-slate-900 hover:bg-slate-800">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create user */}
      <Dialog open={userOpen} onOpenChange={setUserOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-heading">New User</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Full Name</Label>
              <Input data-testid="admin-user-name" value={uForm.name} onChange={(e) => setUForm({ ...uForm, name: e.target.value })} className="mt-1 rounded-md" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email</Label>
                <Input data-testid="admin-user-email" value={uForm.email} onChange={(e) => setUForm({ ...uForm, email: e.target.value })} className="mt-1 rounded-md" />
              </div>
              <div>
                <Label>Password</Label>
                <Input data-testid="admin-user-password" value={uForm.password} onChange={(e) => setUForm({ ...uForm, password: e.target.value })} className="mt-1 rounded-md" />
              </div>
            </div>
            <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
              <span className="text-sm font-medium text-slate-900">Administrator (can manage companies & users)</span>
              <Switch data-testid="admin-user-isadmin" checked={uForm.is_admin} onCheckedChange={(v) => setUForm({ ...uForm, is_admin: v })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-md" onClick={() => setUserOpen(false)}>Cancel</Button>
            <Button data-testid="admin-save-user" onClick={createUser} className="rounded-md bg-slate-900 hover:bg-slate-800">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Grant access */}
      <Dialog open={!!assignFor} onOpenChange={(o) => !o && setAssignFor(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-heading">Grant Company Access · {assignFor?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Company</Label>
              <Select value={assign.company_id} onValueChange={(v) => setAssign({ ...assign, company_id: v })}>
                <SelectTrigger data-testid="assign-company-select" className="mt-1 rounded-md"><SelectValue placeholder="Select company" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {companies.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Role</Label>
              <Select value={assign.role} onValueChange={(v) => setAssign({ ...assign, role: v })}>
                <SelectTrigger data-testid="assign-role-select" className="mt-1 rounded-md capitalize"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="owner">Owner</SelectItem>
                  <SelectItem value="member">Member</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-md" onClick={() => setAssignFor(null)}>Cancel</Button>
            <Button data-testid="confirm-assign-btn" onClick={addMembership} className="rounded-md bg-slate-900 hover:bg-slate-800">Grant Access</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
